import React, { useState } from 'react';
import { ChevronLeft, AlertCircle } from 'lucide-react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { PhotoUpload } from '../components/PhotoUpload';
import { Badge } from '../components/Badge';
interface DisputeProps {
  tripId: string;
  onBack: () => void;
  onComplete: () => void;
}
export function Dispute({
  onBack,
  onComplete
}: DisputeProps) {
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const issueTypes = [{
    value: 'damage',
    label: 'Item Damage',
    description: 'Item was damaged during rental'
  }, {
    value: 'missing',
    label: 'Missing Parts',
    description: 'Parts or accessories are missing'
  }, {
    value: 'late',
    label: 'Late Return',
    description: 'Item was returned late'
  }, {
    value: 'condition',
    label: 'Poor Condition',
    description: 'Item not as described'
  }, {
    value: 'other',
    label: 'Other Issue',
    description: 'Something else went wrong'
  }];
  return <div className="min-h-screen bg-cream pb-24" data-id="element-1020">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-sand" data-id="element-1021">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center" data-id="element-1022">
          <button onClick={onBack} className="p-2 -ml-2" data-id="element-1023">
            <ChevronLeft size={24} className="text-charcoal" data-id="element-1024" />
          </button>
          <h1 className="font-semibold text-charcoal ml-2" data-id="element-1025">Report Issue</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6" data-id="element-1026">
        {/* Warning */}
        <div className="bg-clay/20 border border-clay/30 rounded-ios-lg p-4 flex gap-3" data-id="element-1027">
          <AlertCircle size={20} className="text-clay flex-shrink-0 mt-0.5" data-id="element-1028" />
          <div data-id="element-1029">
            <p className="font-semibold text-charcoal mb-1" data-id="element-1030">
              Before filing a dispute
            </p>
            <p className="text-sm text-charcoal" data-id="element-1031">
              Try contacting the other party first. Many issues can be resolved
              through direct communication.
            </p>
          </div>
        </div>

        {/* Item Info */}
        <div className="bg-white rounded-ios-lg p-4 shadow-ios flex gap-3" data-id="element-1032">
          <img src="https://images.unsplash.com/photo-1504148455328-c376907d081c?w=400&q=80" alt="Item" className="w-16 h-16 rounded-ios object-cover" data-id="element-1033" />
          <div data-id="element-1034">
            <h3 className="font-semibold text-charcoal" data-id="element-1035">
              Professional Power Drill Set
            </h3>
            <p className="text-sm text-moss-gray" data-id="element-1036">
              Rental completed Dec 23, 2024
            </p>
          </div>
        </div>

        {/* Issue Type */}
        <div className="bg-white rounded-ios-lg p-4 shadow-ios space-y-3" data-id="element-1037">
          <h3 className="font-semibold text-charcoal" data-id="element-1038">What went wrong?</h3>
          {issueTypes.map(type => <button key={type.value} onClick={() => setIssueType(type.value)} className={`w-full p-4 rounded-ios-lg border-2 transition-all text-left ${issueType === type.value ? 'border-dark-green bg-dark-green/5' : 'border-sand bg-white'}`} data-id="element-1039">
              <div className="font-medium text-charcoal" data-id="element-1040">{type.label}</div>
              <div className="text-sm text-moss-gray mt-1" data-id="element-1041">
                {type.description}
              </div>
            </button>)}
        </div>

        {/* Description */}
        <div className="bg-white rounded-ios-lg p-4 shadow-ios" data-id="element-1042">
          <Input label="Describe the issue" placeholder="Please provide details about what happened..." value={description} onChange={setDescription} multiline rows={5} data-id="element-1043" />
        </div>

        {/* Evidence */}
        <div className="bg-white rounded-ios-lg p-4 shadow-ios" data-id="element-1044">
          <PhotoUpload photos={photos} onPhotosChange={setPhotos} maxPhotos={5} label="Upload Evidence" data-id="element-1045" />
          <p className="text-xs text-moss-gray mt-3" data-id="element-1046">
            Photos or videos showing the issue help us resolve disputes faster.
          </p>
        </div>

        {/* What Happens Next */}
        <div className="bg-white rounded-ios-lg p-4 shadow-ios space-y-3" data-id="element-1047">
          <h3 className="font-semibold text-charcoal" data-id="element-1048">What happens next?</h3>
          <ol className="space-y-2 text-sm text-charcoal" data-id="element-1049">
            <li className="flex gap-2" data-id="element-1050">
              <span className="font-medium text-dark-green" data-id="element-1051">1.</span>
              <span data-id="element-1052">We'll review your dispute within 24 hours</span>
            </li>
            <li className="flex gap-2" data-id="element-1053">
              <span className="font-medium text-dark-green" data-id="element-1054">2.</span>
              <span data-id="element-1055">The other party will be notified and can respond</span>
            </li>
            <li className="flex gap-2" data-id="element-1056">
              <span className="font-medium text-dark-green" data-id="element-1057">3.</span>
              <span data-id="element-1058">We'll mediate and help reach a fair resolution</span>
            </li>
            <li className="flex gap-2" data-id="element-1059">
              <span className="font-medium text-dark-green" data-id="element-1060">4.</span>
              <span data-id="element-1061">Deposits may be adjusted based on the outcome</span>
            </li>
          </ol>
        </div>

        {/* CTA */}
        <Button variant="primary" fullWidth size="lg" disabled={!issueType || !description.trim() || photos.length === 0} onClick={onComplete} data-id="element-1062">
          Submit Dispute
        </Button>
      </div>
    </div>;
}